import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Shield, Loader2, Flag, Check, Trash2, MessageSquareText, ShoppingBag, Eye, Ban, UserX, ShieldOff, ShieldCheck, AlertTriangle,
} from "lucide-react";
import AdminProductPreviewDialog from "@/components/AdminProductPreviewDialog";
import ReporterDetailDialog from "@/components/ReporterDetailDialog";
import ModReasonDialog from "@/components/ModReasonDialog";
import ModReasonNote from "@/components/ModReasonNote";

const REASON_LABEL = {
  spam: "Spam / scam",
  abuse: "Harassment",
  copyright: "Copyright",
  misleading: "Misleading",
  nsfw: "Sexual / graphic",
  other: "Other",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [previewPid, setPreviewPid] = useState(null);
  const [reporterUid, setReporterUid] = useState(null);
  const [tab, setTab] = useState("reports");
  const [bans, setBans] = useState([]);
  const [bansLoading, setBansLoading] = useState(false);
  const [suspendThreshold, setSuspendThreshold] = useState(3);
  const [sellers, setSellers] = useState([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [highAlertThreshold, setHighAlertThreshold] = useState(7);
  const highAlertCount = reports.filter((r) => r.high_alert).length;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reports");
      setReports(r.data.reports || []);
      if (r.data.suspend_threshold) setSuspendThreshold(r.data.suspend_threshold);
      if (r.data.high_alert_threshold) setHighAlertThreshold(r.data.high_alert_threshold);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Admin only");
        navigate("/app");
      } else {
        toast.error("Couldn't load reports");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const refreshBans = useCallback(async () => {
    setBansLoading(true);
    try {
      const r = await api.get("/admin/reporter-bans");
      setBans(r.data.bans || []);
    } catch {
      toast.error("Couldn't load ban list");
    } finally {
      setBansLoading(false);
    }
  }, []);

  const refreshSellers = useCallback(async () => {
    setSellersLoading(true);
    try {
      const r = await api.get("/admin/suspended-sellers");
      setSellers(r.data.sellers || []);
    } catch {
      toast.error("Couldn't load suspended sellers");
    } finally {
      setSellersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/admin"); return; }
    if (!user.is_admin) { toast.error("Admin only"); navigate("/app"); return; }
    refresh();
  }, [authLoading, user, refresh, navigate]);

  useEffect(() => {
    if (tab === "bans" && user?.is_admin) refreshBans();
    if (tab === "sellers" && user?.is_admin) refreshSellers();
  }, [tab, user, refreshBans, refreshSellers]);

  const unban = async (uid) => {
    setBusyId(`unban_${uid}`);
    try {
      await api.post(`/admin/reporters/${uid}/unban`);
      toast.success("Reporting ban lifted");
      setBans((prev) => prev.filter((b) => b.user_id !== uid));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't unban");
    } finally {
      setBusyId(null);
    }
  };

  const saveBanReason = async (uid, reason) => {
    try {
      await api.patch(`/admin/reporters/${uid}/ban-reason`, { reason });
      setBans((prev) => prev.map((b) => (b.user_id === uid ? { ...b, report_ban_reason: reason } : b)));
      toast.success("Reason saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save reason");
    }
  };

  const saveSuspendReason = async (uid, reason) => {
    try {
      await api.patch(`/admin/sellers/${uid}/suspend-reason`, { reason });
      setSellers((prev) => prev.map((s) => (s.user_id === uid ? { ...s, suspend_reason: reason } : s)));
      setReports((prev) => prev.map((r) => (r.author_id === uid ? { ...r, author_suspend_reason: reason } : r)));
      toast.success("Reason saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save reason");
    }
  };

  const unsuspend = async (uid) => {
    setBusyId(`unsuspend_${uid}`);
    try {
      await api.post(`/admin/sellers/${uid}/unsuspend`);
      toast.success("Suspension lifted");
      setSellers((prev) => prev.filter((s) => s.user_id !== uid));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't unsuspend");
    } finally {
      setBusyId(null);
    }
  };

  const act = async (r, kind) => {
    setBusyId(r.report_id);
    try {
      await api.post(`/admin/reports/${r.report_id}/${kind}`);
      toast.success(kind === "dismiss" ? "Report dismissed" : "Content removed");
      // Pop the report immediately so the admin sees it leave the queue.
      setReports((prev) => prev.filter((x) => x.report_id !== r.report_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const suspendSeller = async (reason) => {
    const r = suspendTarget;
    if (!r) return;
    setBusyId(`suspend_${r.report_id}`);
    try {
      const res = await api.post(`/admin/sellers/${r.author_id}/suspend`, { reason: reason || "" });
      toast.success(`Suspended · ${res.data.archived_listings || 0} listings hidden`);
      setSuspendTarget(null);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't suspend seller");
    } finally {
      setBusyId(null);
    }
  };

  if (authLoading || (!user && !authLoading)) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#9CA3AF] gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const FILTERS = [
    { k: "open", label: "Open" },
    { k: "dismissed", label: "Dismissed" },
    { k: "removed", label: "Removed" },
    { k: "", label: "All" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F4F6]" data-testid="admin-panel">
      <header className="px-4 sm:px-6 py-4 border-b border-[#1E1E1E] flex items-center justify-between sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-10">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <Link to="/app">
            <Button variant="ghost" size="icon" className="text-[#9CA3AF] hover:bg-[#1E1E1E]">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="font-heading text-xl font-bold flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#EF4444]" /> Admin
          </h1>
          <div className="flex items-center gap-1 sm:ml-2 overflow-x-auto min-w-0" data-testid="admin-tabs">
            <button
              type="button"
              data-testid="admin-tab-reports"
              onClick={() => setTab("reports")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0 whitespace-nowrap ${
                tab === "reports" ? "bg-[#1E1E1E] text-[#F3F4F6]" : "text-[#9CA3AF] hover:text-[#F3F4F6]"
              }`}
            >
              Reports{reports.length > 0 ? ` (${reports.length})` : ""}
              {highAlertCount > 0 && (
                <span
                  data-testid="admin-high-alert-tab-dot"
                  className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full bg-[#EF4444] text-white text-[9px] font-bold align-middle"
                  title={`${highAlertCount} note(s) reported more than ${highAlertThreshold} times`}
                >
                  !
                </span>
              )}
            </button>
            <button
              type="button"
              data-testid="admin-tab-bans"
              onClick={() => setTab("bans")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0 whitespace-nowrap ${
                tab === "bans" ? "bg-[#1E1E1E] text-[#F3F4F6]" : "text-[#9CA3AF] hover:text-[#F3F4F6]"
              }`}
            >
              Report bans
            </button>
            <button
              type="button"
              data-testid="admin-tab-sellers"
              onClick={() => setTab("sellers")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0 whitespace-nowrap ${
                tab === "sellers" ? "bg-[#1E1E1E] text-[#F3F4F6]" : "text-[#9CA3AF] hover:text-[#F3F4F6]"
              }`}
            >
              Suspended sellers
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {tab === "bans" ? (
          <BansView bans={bans} loading={bansLoading} busyId={busyId} onUnban={unban} onSaveReason={saveBanReason} />
        ) : tab === "sellers" ? (
          <SuspendedSellersView
            sellers={sellers}
            loading={sellersLoading}
            busyId={busyId}
            onUnsuspend={unsuspend}
            onSaveReason={saveSuspendReason}
          />
        ) : loading ? (
          <div className="flex items-center justify-center py-20 text-[#6B7280] text-sm gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading reports…
          </div>
        ) : reports.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="rounded-2xl bg-[#121212] border border-dashed border-[#27272A] py-20 text-center"
          >
            <div className="mx-auto h-12 w-12 rounded-full bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center mb-4">
              <Shield className="h-5 w-5 text-[#10B981]" />
            </div>
            <h3 className="font-heading text-lg font-semibold mb-1">All clear</h3>
            <p className="text-sm text-[#9CA3AF] max-w-sm mx-auto">
              Nothing in the queue right now. Reports flagged on marketplace listings or comments will show up here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => {
              const isComment = r.target_type === "comment";
              return (
                <article
                  key={r.report_id}
                  data-testid={`admin-report-${r.report_id}`}
                  className={`rounded-2xl border p-4 sm:p-5 ${
                    r.high_alert
                      ? "bg-[#EF4444]/[0.07] border-[#EF4444]/50 ring-1 ring-[#EF4444]/30"
                      : "bg-[#121212] border-[#1E1E1E]"
                  }`}
                >
                  {r.high_alert && (
                    <div
                      data-testid={`admin-high-alert-${r.report_id}`}
                      className="mb-3 flex items-center gap-2 rounded-xl bg-[#EF4444]/15 border border-[#EF4444]/50 px-3 py-2 text-[#FCA5A5] text-xs font-semibold"
                    >
                      <AlertTriangle className="h-4 w-4 shrink-0 animate-pulse" />
                      HIGH ALERT — reported by {r.target_report_count} different people
                    </div>
                  )}
                  <div className="flex flex-wrap items-start gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-xs text-[#9CA3AF]">
                        {isComment ? <MessageSquareText className="h-3.5 w-3.5" /> : <ShoppingBag className="h-3.5 w-3.5" />}
                        <span className="uppercase tracking-wider text-[10px]">{isComment ? "Comment" : "Listing"}</span>
                        <span>·</span>
                        <span className="text-[11px]">{fmtDate(r.created_at)}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-[#F3F4F6] mt-1 line-clamp-2 break-words">
                        {r.content_snippet || "(no preview)"}
                      </h3>
                      {r.product_id && (
                        <button
                          type="button"
                          onClick={() => setPreviewPid(r.product_id)}
                          data-testid={`admin-preview-${r.report_id}`}
                          className="inline-flex items-center gap-1 text-[11px] text-[#3B82F6] hover:text-[#60A5FA] mt-1"
                        >
                          <Eye className="h-3 w-3" /> View contents
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 text-xs">
                    <div className="rounded-xl bg-[#0A0A0A] border border-[#1E1E1E] px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1 flex items-center gap-1">
                        <Flag className="h-3 w-3 text-[#EF4444]" /> Reason
                      </div>
                      <div className="text-[#F3F4F6] font-medium">{REASON_LABEL[r.reason] || r.reason}</div>
                      {r.notes && (
                        <div className="text-[#9CA3AF] mt-1 whitespace-pre-wrap break-words">{r.notes}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => r.reporter_id && setReporterUid(r.reporter_id)}
                      disabled={!r.reporter_id}
                      data-testid={`admin-reporter-${r.report_id}`}
                      className="rounded-xl bg-[#0A0A0A] border border-[#1E1E1E] px-3 py-2 text-left w-full hover:border-[#3B82F6]/60 transition-colors disabled:cursor-default disabled:hover:border-[#1E1E1E]"
                    >
                      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1 flex items-center justify-between">
                        <span>Reporter</span>
                        {r.reporter_id && <span className="text-[#3B82F6] normal-case tracking-normal">view history →</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {r.reporter?.picture ? (
                          <img src={r.reporter.picture} alt="" className="h-6 w-6 rounded-full object-cover border border-[#27272A]" />
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white text-[10px] font-semibold flex items-center justify-center">
                            {(r.reporter?.name || r.reporter?.email || "?").trim().slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-[#F3F4F6] text-sm font-medium truncate">{r.reporter?.name || "Unknown"}</div>
                          <div className="text-[#6B7280] text-[11px] truncate">{r.reporter?.email || r.reporter_email}</div>
                        </div>
                      </div>
                      {/* Troll signal: lifetime accuracy so the admin can weigh this flag */}
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {r.reporter?.report_banned && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#EF4444]/20 text-[#F87171] border border-[#EF4444]/40">
                            <ShieldOff className="h-2.5 w-2.5" /> banned
                          </span>
                        )}
                        {r.reporter?.resolved_reports > 0 ? (
                          <>
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#10B981]/15 text-[#34D399] border border-[#10B981]/30">
                              <Check className="h-2.5 w-2.5" /> {r.reporter.confirmed_reports} confirmed
                            </span>
                            {r.reporter.dismissed_reports > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#EF4444]/15 text-[#F87171] border border-[#EF4444]/30">
                                {r.reporter.dismissed_reports} dismissed
                              </span>
                            )}
                            {r.reporter?.eligible_for_report_ban && !r.reporter?.report_banned && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#FBBF24] border border-[#F59E0B]/30">
                                <UserX className="h-2.5 w-2.5" /> ban eligible
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1E1E1E] text-[#9CA3AF] border border-[#27272A]">
                            First report
                          </span>
                        )}
                      </div>
                    </button>
                  </div>

                  {/* Seller moderation strip — flags repeat offenders with
                      their running count of admin takedowns and a one-click
                      Suspend button once they hit the threshold. */}
                  {r.author && r.author_id && r.author_id !== "house" && (
                    <div
                      data-testid={`admin-seller-strip-${r.report_id}`}
                      className={`mt-3 rounded-xl border px-3 py-2 flex items-center gap-3 ${
                        r.author_suspended
                          ? "bg-[#EF4444]/10 border-[#EF4444]/40"
                          : r.author_eligible_for_suspend
                            ? "bg-[#F59E0B]/10 border-[#F59E0B]/40"
                            : "bg-[#0A0A0A] border-[#1E1E1E]"
                      }`}
                    >
                      {r.author.picture ? (
                        <img src={r.author.picture} alt="" className="h-7 w-7 rounded-full object-cover border border-[#27272A]" />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#6B7280] to-[#1E1E1E] text-white text-[10px] font-semibold flex items-center justify-center">
                          {(r.author.name || r.author.email || "?").trim().slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-[#6B7280] uppercase tracking-wider">Seller</div>
                        <div className="text-sm text-[#F3F4F6] truncate">{r.author.name || r.author.email}</div>
                      </div>
                      <div className="text-right text-[11px] shrink-0">
                        <div className={`font-semibold ${
                          r.author_removed_count >= suspendThreshold ? "text-[#F59E0B]" : "text-[#D1D5DB]"
                        }`}>
                          {r.author_removed_count} removed
                        </div>
                        <div className="text-[#6B7280] text-[10px]">threshold {suspendThreshold}</div>
                      </div>
                      {r.author_suspended ? (
                        <span
                          data-testid={`admin-seller-suspended-${r.report_id}`}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[#EF4444]/20 border border-[#EF4444]/40 text-[#F87171]"
                        >
                          <Ban className="h-3 w-3" /> Suspended
                        </span>
                      ) : r.author_eligible_for_suspend ? (
                        <Button
                          onClick={() => setSuspendTarget(r)}
                          disabled={busyId === `suspend_${r.report_id}`}
                          data-testid={`admin-suspend-seller-${r.report_id}`}
                          className="h-8 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-black text-[11px] font-semibold"
                        >
                          {busyId === `suspend_${r.report_id}` ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <UserX className="h-3 w-3 mr-1" />
                          )}
                          Suspend seller
                        </Button>
                      ) : null}
                    </div>
                  )}
                  {r.author_suspended && r.author_id && r.author_id !== "house" && (
                    <ModReasonNote
                      reason={r.author_suspend_reason}
                      onSave={(reason) => saveSuspendReason(r.author_id, reason)}
                      testId={`admin-suspend-reason-${r.report_id}`}
                    />
                  )}

                  {/* Action buttons — report is dismissed/removed and
                      disappears from the queue in a single click. */}
                  <div className="flex items-center gap-2 pt-4 justify-end">
                      <Button
                        variant="ghost"
                        onClick={() => act(r, "dismiss")}
                        disabled={busyId === r.report_id}
                        data-testid={`admin-dismiss-${r.report_id}`}
                        className="h-9 rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E] text-xs"
                      >
                        {busyId === r.report_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                        Dismiss
                      </Button>
                      <Button
                        onClick={() => act(r, "remove")}
                        disabled={busyId === r.report_id}
                        data-testid={`admin-remove-${r.report_id}`}
                        className="h-9 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white text-xs"
                      >
                        {busyId === r.report_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                        Remove {isComment ? "comment" : "listing"}
                      </Button>
                    </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
      <AdminProductPreviewDialog
        productId={previewPid}
        open={!!previewPid}
        onOpenChange={(o) => !o && setPreviewPid(null)}
      />
      <ReporterDetailDialog
        reporterId={reporterUid}
        open={!!reporterUid}
        onOpenChange={(o) => !o && setReporterUid(null)}
        onChanged={() => { refresh(); if (tab === "bans") refreshBans(); }}
      />
      <ModReasonDialog
        open={!!suspendTarget}
        onOpenChange={(o) => !o && setSuspendTarget(null)}
        title="Suspend seller"
        description={`${suspendTarget?.author?.name || suspendTarget?.author?.email || "This seller"}'s active listings will be hidden from the marketplace and they can't publish new ones until you unsuspend them.`}
        confirmLabel="Suspend seller"
        placeholder="e.g. 3 removed listings for copyright"
        busy={busyId === `suspend_${suspendTarget?.report_id}`}
        onConfirm={suspendSeller}
      />
    </div>
  );
}

const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

function BansView({ bans, loading, busyId, onUnban, onSaveReason }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#6B7280] text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading ban list…
      </div>
    );
  }
  if (!bans.length) {
    return (
      <div data-testid="admin-bans-empty" className="rounded-2xl bg-[#121212] border border-dashed border-[#27272A] py-20 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center mb-4">
          <ShieldCheck className="h-5 w-5 text-[#10B981]" />
        </div>
        <h3 className="font-heading text-lg font-semibold mb-1">No one is banned</h3>
        <p className="text-sm text-[#9CA3AF] max-w-sm mx-auto">
          Users you ban from reporting will appear here. You can lift a ban anytime.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="admin-bans-list">
      {bans.map((b) => (
        <article
          key={b.user_id}
          data-testid={`admin-ban-${b.user_id}`}
          className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-4"
        >
          <div className="flex items-center gap-3">
          {b.picture ? (
            <img src={b.picture} alt="" className="h-10 w-10 rounded-full object-cover border border-[#27272A]" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#EF4444] to-[#7F1D1D] text-white text-sm font-semibold flex items-center justify-center">
              {(b.name || b.email || "?").trim().slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[#F3F4F6] text-sm font-medium truncate">{b.name || "Unknown"}</div>
            <div className="text-[#6B7280] text-[11px] truncate">{b.email}</div>
            <div className="flex items-center gap-2 mt-1 text-[10px] text-[#9CA3AF]">
              <span className="inline-flex items-center gap-1 text-[#F87171]"><ShieldOff className="h-2.5 w-2.5" /> banned {fmtDay(b.report_banned_at)}</span>
              {b.dismissed_reports > 0 && <span>· {b.dismissed_reports} dismissed</span>}
              {b.confirmed_reports > 0 && <span>· {b.confirmed_reports} confirmed</span>}
            </div>
          </div>
          <Button
            onClick={() => onUnban(b.user_id)}
            disabled={busyId === `unban_${b.user_id}`}
            data-testid={`admin-unban-${b.user_id}`}
            variant="ghost"
            className="h-9 rounded-full text-[#34D399] hover:text-[#34D399] hover:bg-[#10B981]/10 text-xs shrink-0"
          >
            {busyId === `unban_${b.user_id}` ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
            Unban
          </Button>
          </div>
          <ModReasonNote
            reason={b.report_ban_reason}
            onSave={(reason) => onSaveReason(b.user_id, reason)}
            testId={`admin-ban-reason-${b.user_id}`}
          />
        </article>
      ))}
    </div>
  );
}

function SuspendedSellersView({ sellers, loading, busyId, onUnsuspend, onSaveReason }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#6B7280] text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading suspended sellers…
      </div>
    );
  }
  if (!sellers.length) {
    return (
      <div data-testid="admin-sellers-empty" className="rounded-2xl bg-[#121212] border border-dashed border-[#27272A] py-20 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center mb-4">
          <ShieldCheck className="h-5 w-5 text-[#10B981]" />
        </div>
        <h3 className="font-heading text-lg font-semibold mb-1">No suspended sellers</h3>
        <p className="text-sm text-[#9CA3AF] max-w-sm mx-auto">
          Sellers you suspend show up here with your reason note. You can lift a suspension anytime.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="admin-sellers-list">
      {sellers.map((s) => (
        <article
          key={s.user_id}
          data-testid={`admin-suspended-${s.user_id}`}
          className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-4"
        >
          <div className="flex items-center gap-3">
            {s.picture ? (
              <img src={s.picture} alt="" className="h-10 w-10 rounded-full object-cover border border-[#27272A]" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#7C2D12] text-white text-sm font-semibold flex items-center justify-center">
                {(s.name || s.email || "?").trim().slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[#F3F4F6] text-sm font-medium truncate">{s.name || "Unknown"}</div>
              <div className="text-[#6B7280] text-[11px] truncate">{s.email}</div>
              <div className="flex items-center gap-2 mt-1 text-[10px] text-[#9CA3AF]">
                <span className="inline-flex items-center gap-1 text-[#F87171]"><Ban className="h-2.5 w-2.5" /> suspended {fmtDay(s.suspended_at)}</span>
                {s.removed_count > 0 && <span>· {s.removed_count} removed listings</span>}
              </div>
            </div>
            <Button
              onClick={() => onUnsuspend(s.user_id)}
              disabled={busyId === `unsuspend_${s.user_id}`}
              data-testid={`admin-unsuspend-${s.user_id}`}
              variant="ghost"
              className="h-9 rounded-full text-[#34D399] hover:text-[#34D399] hover:bg-[#10B981]/10 text-xs shrink-0"
            >
              {busyId === `unsuspend_${s.user_id}` ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
              Unsuspend
            </Button>
          </div>
          <ModReasonNote
            reason={s.suspend_reason}
            onSave={(reason) => onSaveReason(s.user_id, reason)}
            testId={`admin-suspend-note-${s.user_id}`}
          />
        </article>
      ))}
    </div>
  );
}
